/* eslint-disable react/no-unknown-property */
/**
 * Dedicated low-poly 3D building components for Silva City township.
 *
 * Every amenity has a unique, recognisable structure (no more generic pins).
 * All buildings are stylised and use the brand palette (gold + emerald +
 * cream marble + saffron + glass). Each component accepts standard mesh
 * props (position, rotation, scale).
 */

import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";

// =============================================================
// MATERIAL PALETTE
// =============================================================
export const M = {
  // Brand
  gold: "#D4AF37",
  goldBright: "#E7C36B",
  goldDeep: "#8a6f1a",
  emerald: "#046307",
  emeraldBright: "#1a9a4f",
  // Architecture
  cream: "#f0e6d2",
  warmWhite: "#e8d9b8",
  marble: "#dcd4c0",
  stone: "#6b6960",
  stoneLight: "#8f8c80",
  redTile: "#8b3a3a",
  saffron: "#cc7722",
  saffronDeep: "#8b4513",
  wood: "#5a4231",
  // Surfaces
  glass: "#1a3540",
  glassLight: "#2c5f74",
  glassBright: "#4d8aa3",
  // Nature
  grass: "#2a4a2a",
  grassBright: "#48a04e",
  grassMid: "#3a7a3a",
  sand: "#c9a876",
  water: "#1f6884",
  waterBright: "#3aa0c2",
  // Roads
  road: "#1a1a1c",
  roadDark: "#101012",
  roadMark: "#f5e6a8",
  roadMarkWhite: "#e8e3d0",
};

// =============================================================
// CLUBHOUSE — modern flat-roof building + pool
// =============================================================
export const Clubhouse = (props) => (
  <group {...props}>
    {/* Marble plinth */}
    <mesh position={[0, 0.07, 0]} castShadow receiveShadow>
      <boxGeometry args={[3.6, 0.14, 2.8]} />
      <meshStandardMaterial color={M.marble} roughness={0.55} metalness={0.05} />
    </mesh>
    {/* Main building */}
    <mesh position={[0, 0.65, -0.1]} castShadow receiveShadow>
      <boxGeometry args={[3.0, 1.1, 2.1]} />
      <meshStandardMaterial color={M.warmWhite} roughness={0.62} />
    </mesh>
    {/* Glass facade — front */}
    <mesh position={[0, 0.65, 0.96]}>
      <planeGeometry args={[2.7, 0.85]} />
      <meshStandardMaterial
        color={M.glassLight}
        emissive={M.glassBright}
        emissiveIntensity={0.55}
        roughness={0.15}
        metalness={0.45}
      />
    </mesh>
    {/* Glass facade — back */}
    <mesh position={[0, 0.65, -1.16]} rotation={[0, Math.PI, 0]}>
      <planeGeometry args={[2.7, 0.85]} />
      <meshStandardMaterial
        color={M.glassLight}
        emissive={M.glassBright}
        emissiveIntensity={0.4}
      />
    </mesh>
    {/* Gold cornice trim */}
    <mesh position={[0, 1.22, -0.1]} castShadow>
      <boxGeometry args={[3.1, 0.08, 2.2]} />
      <meshStandardMaterial color={M.gold} metalness={0.85} roughness={0.25} />
    </mesh>
    {/* Roof */}
    <mesh position={[0, 1.32, -0.1]} castShadow receiveShadow>
      <boxGeometry args={[3.05, 0.12, 2.15]} />
      <meshStandardMaterial color={M.stoneLight} roughness={0.55} />
    </mesh>
    {/* Entrance canopy */}
    <mesh position={[0, 0.28, 1.18]} castShadow>
      <boxGeometry args={[1.4, 0.05, 0.4]} />
      <meshStandardMaterial color={M.gold} metalness={0.7} roughness={0.3} />
    </mesh>
    {[-0.55, 0.55].map((x) => (
      <mesh key={x} position={[x, 0.14, 1.18]} castShadow>
        <cylinderGeometry args={[0.05, 0.05, 0.28, 8]} />
        <meshStandardMaterial color={M.gold} metalness={0.8} />
      </mesh>
    ))}
    {/* Pool (front-left) */}
    <mesh position={[0, 0.11, 1.85]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[2.6, 1.0]} />
      <meshStandardMaterial
        color={M.water}
        emissive={M.waterBright}
        emissiveIntensity={0.35}
        roughness={0.25}
        metalness={0.4}
      />
    </mesh>
    {/* Pool deck rim */}
    <mesh position={[0, 0.1, 1.85]} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[0.0, 1.45, 4, 1, 0, Math.PI * 2]} />
      <meshBasicMaterial visible={false} />
    </mesh>
    {/* Lounge umbrellas */}
    {[
      [-1.0, 1.85],
      [1.0, 1.85],
    ].map(([x, z]) => (
      <group key={`umbrella-${x}-${z}`} position={[x, 0, z]}>
        <mesh position={[0, 0.3, 0]}>
          <cylinderGeometry args={[0.03, 0.03, 0.6, 6]} />
          <meshStandardMaterial color={M.wood} />
        </mesh>
        <mesh position={[0, 0.6, 0]} castShadow>
          <coneGeometry args={[0.3, 0.12, 8]} />
          <meshStandardMaterial color={M.saffron} roughness={0.7} />
        </mesh>
      </group>
    ))}
  </group>
);

// =============================================================
// TEMPLE — Indian temple with shikara/spire
// =============================================================
export const Temple = (props) => (
  <group {...props}>
    {/* Marble platform with steps */}
    <mesh position={[0, 0.08, 0]} castShadow receiveShadow>
      <boxGeometry args={[2.4, 0.16, 2.4]} />
      <meshStandardMaterial color={M.marble} roughness={0.5} />
    </mesh>
    <mesh position={[0, 0.21, 0]} castShadow receiveShadow>
      <boxGeometry args={[2.0, 0.1, 2.0]} />
      <meshStandardMaterial color={M.cream} roughness={0.55} />
    </mesh>
    {/* Garbhagriha (sanctum) */}
    <mesh position={[0, 0.6, 0]} castShadow receiveShadow>
      <boxGeometry args={[1.4, 0.7, 1.4]} />
      <meshStandardMaterial color={M.cream} roughness={0.6} />
    </mesh>
    {/* Gold band */}
    <mesh position={[0, 0.96, 0]} castShadow>
      <boxGeometry args={[1.45, 0.06, 1.45]} />
      <meshStandardMaterial color={M.gold} metalness={0.85} roughness={0.25} />
    </mesh>
    {/* Mid tower */}
    <mesh position={[0, 1.2, 0]} castShadow>
      <boxGeometry args={[1.0, 0.4, 1.0]} />
      <meshStandardMaterial color={M.cream} roughness={0.6} />
    </mesh>
    {/* Shikara spire — multi-tier */}
    <mesh position={[0, 1.65, 0]} castShadow>
      <coneGeometry args={[0.55, 0.6, 4]} />
      <meshStandardMaterial color={M.saffron} roughness={0.45} />
    </mesh>
    <mesh position={[0, 2.0, 0]} castShadow>
      <coneGeometry args={[0.35, 0.45, 4]} />
      <meshStandardMaterial color={M.saffronDeep} roughness={0.5} />
    </mesh>
    {/* Kalash finial */}
    <mesh position={[0, 2.32, 0]} castShadow>
      <sphereGeometry args={[0.11, 12, 12]} />
      <meshStandardMaterial color={M.gold} metalness={0.95} roughness={0.15} />
    </mesh>
    <mesh position={[0, 2.46, 0]} castShadow>
      <coneGeometry args={[0.06, 0.18, 8]} />
      <meshStandardMaterial color={M.gold} metalness={0.9} />
    </mesh>
    {/* Door (entrance) */}
    <mesh position={[0, 0.55, 0.71]}>
      <planeGeometry args={[0.36, 0.55]} />
      <meshStandardMaterial color={M.saffronDeep} />
    </mesh>
    <mesh position={[0, 0.85, 0.72]}>
      <planeGeometry args={[0.46, 0.06]} />
      <meshStandardMaterial color={M.gold} metalness={0.7} />
    </mesh>
    {/* Pillared portico columns */}
    {[
      [-0.55, 0.7],
      [0.55, 0.7],
    ].map(([x, z]) => (
      <mesh key={`pillar-${x}-${z}`} position={[x, 0.5, z]} castShadow>
        <cylinderGeometry args={[0.07, 0.08, 0.7, 8]} />
        <meshStandardMaterial color={M.cream} roughness={0.6} />
      </mesh>
    ))}
    {/* Smaller side spires */}
    {[
      [-0.85, 0],
      [0.85, 0],
    ].map(([x, z]) => (
      <group key={`side-spire-${x}-${z}`} position={[x, 0, z]}>
        <mesh position={[0, 0.5, 0]} castShadow>
          <boxGeometry args={[0.3, 0.6, 0.3]} />
          <meshStandardMaterial color={M.cream} roughness={0.6} />
        </mesh>
        <mesh position={[0, 0.95, 0]} castShadow>
          <coneGeometry args={[0.18, 0.36, 4]} />
          <meshStandardMaterial color={M.saffron} roughness={0.45} />
        </mesh>
      </group>
    ))}
  </group>
);

// =============================================================
// PLAYGROUND — slide, swings, see-saw on sand pad
// =============================================================
export const Playground = (props) => (
  <group {...props}>
    {/* Sand pad */}
    <mesh
      position={[0, 0.025, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      receiveShadow
    >
      <circleGeometry args={[2.0, 32]} />
      <meshStandardMaterial color={M.sand} roughness={0.9} />
    </mesh>
    {/* Border ring */}
    <mesh
      position={[0, 0.035, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
    >
      <ringGeometry args={[1.95, 2.05, 32]} />
      <meshStandardMaterial color={M.wood} roughness={0.7} />
    </mesh>

    {/* Slide */}
    <group position={[-0.7, 0, 0.3]}>
      <mesh position={[0, 0.6, 0]} castShadow>
        <boxGeometry args={[0.12, 1.2, 0.12]} />
        <meshStandardMaterial color={M.gold} metalness={0.75} roughness={0.3} />
      </mesh>
      <mesh position={[0.05, 1.2, 0]} castShadow>
        <boxGeometry args={[0.45, 0.05, 0.45]} />
        <meshStandardMaterial color={M.wood} />
      </mesh>
      <mesh
        position={[0.45, 0.55, 0]}
        rotation={[0, 0, -Math.PI / 4]}
        castShadow
      >
        <boxGeometry args={[0.9, 0.06, 0.32]} />
        <meshStandardMaterial
          color={M.saffron}
          metalness={0.5}
          roughness={0.4}
        />
      </mesh>
      {/* Ladder steps */}
      {[0.2, 0.45, 0.7, 0.95].map((y) => (
        <mesh key={`ladder-${y}`} position={[-0.18, y, 0]} castShadow>
          <boxGeometry args={[0.18, 0.03, 0.18]} />
          <meshStandardMaterial color={M.wood} />
        </mesh>
      ))}
    </group>

    {/* Swing set */}
    <group position={[0.5, 0, 0.4]}>
      {/* A-frame legs */}
      {[-0.4, 0.4].map((x) => (
        <group key={`leg-${x}`} position={[x, 0, 0]}>
          <mesh
            position={[0, 0.6, -0.15]}
            rotation={[Math.PI / 12, 0, 0]}
            castShadow
          >
            <cylinderGeometry args={[0.04, 0.04, 1.25, 8]} />
            <meshStandardMaterial color={M.gold} metalness={0.7} />
          </mesh>
          <mesh
            position={[0, 0.6, 0.15]}
            rotation={[-Math.PI / 12, 0, 0]}
            castShadow
          >
            <cylinderGeometry args={[0.04, 0.04, 1.25, 8]} />
            <meshStandardMaterial color={M.gold} metalness={0.7} />
          </mesh>
        </group>
      ))}
      {/* Top bar */}
      <mesh
        position={[0, 1.18, 0]}
        rotation={[0, 0, Math.PI / 2]}
        castShadow
      >
        <cylinderGeometry args={[0.04, 0.04, 1.0, 8]} />
        <meshStandardMaterial color={M.gold} metalness={0.7} />
      </mesh>
      {/* Two swings */}
      {[-0.25, 0.25].map((x) => (
        <group key={`swing-${x}`} position={[x, 0, 0]}>
          <mesh position={[0, 0.7, 0]}>
            <boxGeometry args={[0.018, 0.85, 0.018]} />
            <meshStandardMaterial color={M.stone} />
          </mesh>
          <mesh position={[0, 0.3, 0]} castShadow>
            <boxGeometry args={[0.2, 0.04, 0.22]} />
            <meshStandardMaterial color={M.saffron} />
          </mesh>
        </group>
      ))}
    </group>

    {/* See-saw */}
    <group position={[0.45, 0, -0.85]}>
      <mesh position={[0, 0.15, 0]} castShadow>
        <boxGeometry args={[0.12, 0.3, 0.12]} />
        <meshStandardMaterial color={M.stone} />
      </mesh>
      <mesh position={[0, 0.32, 0]} rotation={[0, 0, Math.PI / 12]} castShadow>
        <boxGeometry args={[1.4, 0.06, 0.16]} />
        <meshStandardMaterial color={M.emeraldBright} metalness={0.3} />
      </mesh>
    </group>

    {/* Sandpit toy / pyramid */}
    <mesh position={[-0.8, 0.18, -0.6]} castShadow>
      <coneGeometry args={[0.25, 0.36, 6]} />
      <meshStandardMaterial color={M.saffron} roughness={0.7} />
    </mesh>
  </group>
);

// =============================================================
// AMPHITHEATER — tiered semicircle seating + stage + backdrop
// =============================================================
export const Amphitheater = (props) => (
  <group {...props}>
    {/* Stage (half-circle) */}
    <mesh
      position={[0, 0.1, 0.8]}
      castShadow
      receiveShadow
      rotation={[0, 0, 0]}
    >
      <cylinderGeometry
        args={[0.95, 0.95, 0.2, 32, 1, false, 0, Math.PI]}
      />
      <meshStandardMaterial color={M.marble} roughness={0.5} />
    </mesh>
    {/* Stage floor accent */}
    <mesh position={[0, 0.21, 0.8]} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[0.0, 0.92, 32, 1, 0, Math.PI]} />
      <meshStandardMaterial color={M.wood} roughness={0.6} />
    </mesh>
    {/* Backdrop / proscenium */}
    <mesh position={[0, 0.85, 1.45]} castShadow>
      <boxGeometry args={[2.0, 1.5, 0.18]} />
      <meshStandardMaterial color={M.warmWhite} roughness={0.6} />
    </mesh>
    {/* Backdrop gold trim */}
    <mesh position={[0, 1.55, 1.45]} castShadow>
      <boxGeometry args={[2.1, 0.08, 0.22]} />
      <meshStandardMaterial color={M.gold} metalness={0.85} roughness={0.2} />
    </mesh>
    {/* Tiered seats (concentric half-tori) */}
    {[0, 1, 2, 3, 4].map((tier) => (
      <group key={`tier-${tier}`}>
        <mesh
          position={[0, 0.12 + tier * 0.13, -0.05 - tier * 0.32]}
          rotation={[0, Math.PI, 0]}
          castShadow
          receiveShadow
        >
          <torusGeometry
            args={[0.85 + tier * 0.45, 0.07, 8, 32, Math.PI]}
          />
          <meshStandardMaterial color={M.stone} roughness={0.7} />
        </mesh>
      </group>
    ))}
    {/* Stage spotlights */}
    {[-0.7, 0.7].map((x) => (
      <mesh key={`spotlight-${x}`} position={[x, 0.7, 1.25]} castShadow>
        <sphereGeometry args={[0.08, 12, 12]} />
        <meshStandardMaterial
          color={M.gold}
          emissive={M.goldBright}
          emissiveIntensity={1.2}
        />
      </mesh>
    ))}
  </group>
);

// =============================================================
// SOCIAL CENTRE — 2-story modern building with glass facade
// =============================================================
export const SocialCentre = (props) => (
  <group {...props}>
    {/* Plinth */}
    <mesh position={[0, 0.05, 0]} castShadow receiveShadow>
      <boxGeometry args={[2.8, 0.1, 2.2]} />
      <meshStandardMaterial color={M.marble} roughness={0.55} />
    </mesh>
    {/* Ground floor */}
    <mesh position={[0, 0.55, 0]} castShadow receiveShadow>
      <boxGeometry args={[2.3, 0.9, 1.7]} />
      <meshStandardMaterial color={M.warmWhite} roughness={0.6} />
    </mesh>
    {/* Glass curtain — front */}
    <mesh position={[0, 0.55, 0.86]}>
      <planeGeometry args={[2.0, 0.75]} />
      <meshStandardMaterial
        color={M.glassLight}
        emissive={M.glassBright}
        emissiveIntensity={0.55}
        roughness={0.15}
        metalness={0.45}
      />
    </mesh>
    {/* Mid trim */}
    <mesh position={[0, 1.02, 0]} castShadow>
      <boxGeometry args={[2.35, 0.06, 1.75]} />
      <meshStandardMaterial color={M.gold} metalness={0.85} />
    </mesh>
    {/* Upper floor (slightly recessed) */}
    <mesh position={[0, 1.5, 0]} castShadow receiveShadow>
      <boxGeometry args={[2.0, 0.85, 1.45]} />
      <meshStandardMaterial color={M.cream} roughness={0.6} />
    </mesh>
    {/* Upper glass band */}
    <mesh position={[0, 1.5, 0.73]}>
      <planeGeometry args={[1.7, 0.6]} />
      <meshStandardMaterial
        color={M.glassLight}
        emissive={M.glassBright}
        emissiveIntensity={0.45}
        metalness={0.4}
      />
    </mesh>
    {/* Roof slab */}
    <mesh position={[0, 1.95, 0]} castShadow>
      <boxGeometry args={[2.1, 0.1, 1.55]} />
      <meshStandardMaterial color={M.stoneLight} roughness={0.5} />
    </mesh>
    {/* Roof gold sign */}
    <mesh position={[0, 2.05, 0]} castShadow>
      <boxGeometry args={[1.2, 0.15, 0.05]} />
      <meshStandardMaterial
        color={M.gold}
        emissive={M.gold}
        emissiveIntensity={0.5}
        metalness={0.9}
      />
    </mesh>
    {/* Entrance steps */}
    <mesh position={[0, 0.13, 1.0]} castShadow>
      <boxGeometry args={[0.7, 0.06, 0.3]} />
      <meshStandardMaterial color={M.marble} />
    </mesh>
  </group>
);

// =============================================================
// WELLNESS CENTRE — circular drum + dome + outdoor pool
// =============================================================
export const WellnessCentre = (props) => (
  <group {...props}>
    {/* Plinth */}
    <mesh position={[0, 0.05, 0]} castShadow receiveShadow>
      <boxGeometry args={[2.6, 0.1, 2.6]} />
      <meshStandardMaterial color={M.marble} roughness={0.55} />
    </mesh>
    {/* Cylindrical body */}
    <mesh position={[0, 0.7, -0.2]} castShadow receiveShadow>
      <cylinderGeometry args={[0.95, 0.95, 1.3, 24]} />
      <meshStandardMaterial color={M.warmWhite} roughness={0.6} />
    </mesh>
    {/* Glass band */}
    <mesh position={[0, 0.95, -0.2]}>
      <cylinderGeometry args={[0.97, 0.97, 0.4, 32]} />
      <meshStandardMaterial
        color={M.glassBright}
        emissive={M.glassBright}
        emissiveIntensity={0.55}
        roughness={0.15}
        metalness={0.55}
      />
    </mesh>
    {/* Dome roof */}
    <mesh position={[0, 1.35, -0.2]} castShadow>
      <sphereGeometry
        args={[0.95, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2]}
      />
      <meshStandardMaterial
        color={M.gold}
        metalness={0.85}
        roughness={0.25}
      />
    </mesh>
    {/* Finial spike */}
    <mesh position={[0, 2.4, -0.2]} castShadow>
      <coneGeometry args={[0.06, 0.2, 8]} />
      <meshStandardMaterial color={M.gold} metalness={0.95} />
    </mesh>
    {/* Outdoor lap pool (rectangular) */}
    <mesh
      position={[0, 0.11, 1.4]}
      rotation={[-Math.PI / 2, 0, 0]}
      receiveShadow
    >
      <planeGeometry args={[2.2, 0.95]} />
      <meshStandardMaterial
        color={M.water}
        emissive={M.waterBright}
        emissiveIntensity={0.35}
        metalness={0.4}
        roughness={0.2}
      />
    </mesh>
    {/* Pool deck rim */}
    <mesh
      position={[0, 0.115, 1.4]}
      rotation={[-Math.PI / 2, 0, 0]}
    >
      <ringGeometry args={[1.18, 1.25, 8]} />
      <meshStandardMaterial color={M.marble} />
    </mesh>
    {/* Cabanas */}
    {[
      [-1.2, 1.4],
      [1.2, 1.4],
    ].map(([x, z]) => (
      <group key={`cabana-${x}-${z}`} position={[x, 0, z]}>
        <mesh position={[0, 0.25, 0]}>
          <cylinderGeometry args={[0.03, 0.03, 0.5, 6]} />
          <meshStandardMaterial color={M.wood} />
        </mesh>
        <mesh position={[0, 0.55, 0]} castShadow>
          <coneGeometry args={[0.32, 0.14, 8]} />
          <meshStandardMaterial color={M.emerald} roughness={0.7} />
        </mesh>
      </group>
    ))}
  </group>
);

// =============================================================
// ENTRANCE GATE — two pillars + arch + sign + security cabin
// =============================================================
export const EntranceGate = (props) => (
  <group {...props}>
    {/* Two pillars */}
    {[-1.4, 1.4].map((x) => (
      <group key={`gate-pillar-${x}`} position={[x, 0, 0]}>
        <mesh position={[0, 0.95, 0]} castShadow receiveShadow>
          <boxGeometry args={[0.45, 1.9, 0.45]} />
          <meshStandardMaterial color={M.cream} roughness={0.55} />
        </mesh>
        {/* Cap */}
        <mesh position={[0, 1.95, 0]} castShadow>
          <boxGeometry args={[0.6, 0.12, 0.6]} />
          <meshStandardMaterial color={M.gold} metalness={0.85} />
        </mesh>
        {/* Decorative ring */}
        <mesh
          position={[0, 1.1, 0.23]}
          rotation={[Math.PI / 2, 0, 0]}
        >
          <ringGeometry args={[0.12, 0.18, 16]} />
          <meshStandardMaterial
            color={M.gold}
            emissive={M.gold}
            emissiveIntensity={0.4}
            metalness={0.85}
          />
        </mesh>
      </group>
    ))}
    {/* Top arch beam */}
    <mesh position={[0, 2.05, 0]} castShadow>
      <boxGeometry args={[3.4, 0.2, 0.35]} />
      <meshStandardMaterial color={M.cream} roughness={0.55} />
    </mesh>
    {/* Sign block with project name */}
    <mesh position={[0, 2.3, 0]} castShadow>
      <boxGeometry args={[2.7, 0.32, 0.12]} />
      <meshStandardMaterial
        color={M.gold}
        emissive={M.goldBright}
        emissiveIntensity={0.7}
        metalness={0.9}
        roughness={0.2}
      />
    </mesh>
    {/* Security cabin */}
    <mesh
      position={[2.3, 0.5, 0.0]}
      castShadow
      receiveShadow
    >
      <boxGeometry args={[0.9, 1.0, 0.8]} />
      <meshStandardMaterial color={M.warmWhite} roughness={0.6} />
    </mesh>
    {/* Cabin roof */}
    <mesh position={[2.3, 1.05, 0]} castShadow>
      <boxGeometry args={[1.05, 0.1, 0.95]} />
      <meshStandardMaterial color={M.redTile} roughness={0.55} />
    </mesh>
    {/* Cabin window */}
    <mesh position={[2.3, 0.65, 0.41]}>
      <planeGeometry args={[0.6, 0.35]} />
      <meshStandardMaterial
        color={M.glassBright}
        emissive={M.glassBright}
        emissiveIntensity={0.6}
      />
    </mesh>
    {/* Boom barrier */}
    <mesh position={[0, 0.65, -0.8]} castShadow>
      <cylinderGeometry args={[0.05, 0.05, 0.15, 8]} />
      <meshStandardMaterial color={M.gold} metalness={0.7} />
    </mesh>
    <mesh
      position={[-0.75, 0.7, -0.8]}
      rotation={[0, 0, Math.PI / 2]}
      castShadow
    >
      <cylinderGeometry args={[0.04, 0.04, 1.4, 8]} />
      <meshStandardMaterial color={M.gold} metalness={0.7} />
    </mesh>
  </group>
);

// =============================================================
// PLOTS / RESIDENTIAL HOUSES — Small detached homes for plot zones
// =============================================================
const houseColors = [M.cream, M.warmWhite, "#f0e6d2", "#e5d8b8", "#f5ead0"];
const roofColors = [M.redTile, "#8b3a3a", "#9d4444", "#8b3a3a", "#a04848"];

export const PlotHouse = ({ seed = 0, ...props }) => {
  const houseColor = houseColors[seed % houseColors.length];
  const roofColor = roofColors[seed % roofColors.length];
  // Use seed to vary house slightly
  const w = 0.45 + ((seed * 0.073) % 0.18);
  const d = 0.4 + ((seed * 0.097) % 0.15);
  const h = 0.32 + ((seed * 0.041) % 0.12);

  return (
    <group {...props}>
      <mesh position={[0, h / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color={houseColor} roughness={0.7} />
      </mesh>
      {/* Pitched roof */}
      <mesh
        position={[0, h + 0.09, 0]}
        rotation={[0, Math.PI / 4, 0]}
        castShadow
      >
        <coneGeometry args={[Math.max(w, d) * 0.78, 0.18, 4]} />
        <meshStandardMaterial color={roofColor} roughness={0.55} />
      </mesh>
    </group>
  );
};

// =============================================================
// PARK — landscaped trees on grass patch
// =============================================================
export const Tree = ({ scale = 1, ...props }) => (
  <group {...props} scale={scale}>
    {/* Trunk */}
    <mesh position={[0, 0.18, 0]} castShadow>
      <cylinderGeometry args={[0.04, 0.05, 0.36, 6]} />
      <meshStandardMaterial color={M.wood} roughness={0.85} />
    </mesh>
    {/* Foliage */}
    <mesh position={[0, 0.55, 0]} castShadow>
      <coneGeometry args={[0.28, 0.65, 8]} />
      <meshStandardMaterial color={M.grassMid} roughness={0.85} />
    </mesh>
    <mesh position={[0, 0.85, 0]} castShadow>
      <coneGeometry args={[0.2, 0.45, 8]} />
      <meshStandardMaterial color={M.grassBright} roughness={0.85} />
    </mesh>
  </group>
);

// Decorative round park pavilion (for Park 2 etc.)
export const ParkPavilion = (props) => (
  <group {...props}>
    {/* Floor */}
    <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <circleGeometry args={[0.9, 24]} />
      <meshStandardMaterial color={M.marble} roughness={0.55} />
    </mesh>
    {/* Columns */}
    {Array.from({ length: 8 }).map((_, i) => {
      const a = (i / 8) * Math.PI * 2;
      return (
        <mesh
          key={`column-${a.toFixed(3)}`}
          position={[Math.cos(a) * 0.7, 0.45, Math.sin(a) * 0.7]}
          castShadow
        >
          <cylinderGeometry args={[0.05, 0.05, 0.9, 8]} />
          <meshStandardMaterial color={M.cream} roughness={0.55} />
        </mesh>
      );
    })}
    {/* Dome */}
    <mesh position={[0, 1.0, 0]} castShadow>
      <sphereGeometry
        args={[0.95, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2]}
      />
      <meshStandardMaterial color={M.gold} metalness={0.85} roughness={0.25} />
    </mesh>
    {/* Finial */}
    <mesh position={[0, 1.65, 0]} castShadow>
      <coneGeometry args={[0.06, 0.18, 6]} />
      <meshStandardMaterial color={M.gold} metalness={0.95} />
    </mesh>
  </group>
);

// =============================================================
// FLOATING MARKER — small floating gold orb above each building
// (replaces the old "pin" — clickable, marks each building)
// =============================================================
export const FloatingMarker = ({
  color = M.gold,
  active = false,
  hovered = false,
  height = 3.4,
}) => {
  const ref = useRef();
  useFrame(({ clock }) => {
    if (ref.current) {
      const t = clock.getElapsedTime();
      ref.current.position.y = height + Math.sin(t * 1.6) * 0.18;
      ref.current.rotation.y = t * 0.5;
    }
  });
  return (
    <group ref={ref} position={[0, height, 0]}>
      <mesh castShadow>
        <sphereGeometry args={[active || hovered ? 0.22 : 0.16, 16, 16]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={1.6}
          metalness={0.7}
          roughness={0.2}
        />
      </mesh>
      {/* Ring */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.25, 0.33, 32]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={active || hovered ? 0.95 : 0.55}
          side={2}
        />
      </mesh>
    </group>
  );
};
